import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import majuThumbnail from "@/assets/maju-thumbnail.png";
import maju1 from "@/assets/maju-1.png";
import maju2 from "@/assets/maju-2.png";
import maju3 from "@/assets/maju-3.png";
import maju4 from "@/assets/maju-4.png";
import maju5 from "@/assets/maju-5.png";
import maju6 from "@/assets/maju-6.png";
import tcuberThumbnail from "@/assets/tcuber-thumbnail.png";
import tcuber1 from "@/assets/tcuber-1.png";
import tcuber2 from "@/assets/tcuber-2.png";
import tcuber3 from "@/assets/tcuber-3.png";
import tcuber4 from "@/assets/tcuber-4.png";
import tcuber5 from "@/assets/tcuber-5.png";
import penguinThumbnail from "@/assets/penguin-analysis-thumbnail.png";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PenguinExplorer from "@/components/PenguinExplorer";
import { useState, useRef } from "react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Checkbox } from "@/components/ui/checkbox";
import html2pdf from "html2pdf.js";
import { getExtraProjects, groupForProject, PROJECT_GROUPS, type ProjectGroup } from "@/data/projects";

const Portfolio = () => {
  const { t, language } = useLanguage();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [showPenguinExplorer, setShowPenguinExplorer] = useState(false);
  const [showPdfSelector, setShowPdfSelector] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const carouselApisRef = useRef<{ [key: string]: CarouselApi }>({});
  const [currentSlides, setCurrentSlides] = useState<{ [key: string]: number }>({});
  const [activeGroup, setActiveGroup] = useState<ProjectGroup | "all">("all");

  // Keyed by project id, not grid position: filtering reorders the grid, and a
  // position-keyed ref would hand a card the previous occupant's carousel.
  const handleCarouselApiSet = (id: string, api: CarouselApi) => {
    if (!api || carouselApisRef.current[id]) return;

    carouselApisRef.current[id] = api;

    const handleSelect = () => {
      setCurrentSlides((prev) => ({ ...prev, [id]: api.selectedScrollSnap() }));
    };

    handleSelect();
    api.on("select", handleSelect);
  };

  const allProjects = [
    {
      id: "mapanfurniture",
      title: t.portfolio.webProjects[0].title,
      description: t.portfolio.webProjects[0].description,
      link: "https://mapanfurniture.vercel.app",
      thumbnail: majuThumbnail,
      galleryImages: [maju1, maju2, maju3, maju4, maju5, maju6],
      tags: ["Web", "Landing page", "UI", "Furniture"],
    },
    {
      id: "tcuberstore",
      title: t.portfolio.webProjects[1].title,
      description: t.portfolio.webProjects[1].description,
      link: "https://tcuberstore.vercel.app",
      thumbnail: tcuberThumbnail,
      galleryImages: [tcuber1, tcuber2, tcuber3, tcuber4, tcuber5],
      tags: ["Web", "Landing page", "UI", "Gaming", "Store"],
    },
    {
      id: "penguin-analysis",
      title: t.portfolio.dataProjects[0].title,
      description: t.portfolio.dataProjects[0].description,
      link: null,
      thumbnail: penguinThumbnail,
      galleryImages: [],
      tags: ["Data Analysis", "AI", "Visualization", "React"],
      isInteractive: true,
    },
    ...getExtraProjects(language),
  ];

  const groupCounts = allProjects.reduce<Record<string, number>>((acc, p) => {
    const g = groupForProject(p.id);
    acc[g] = (acc[g] ?? 0) + 1;
    return acc;
  }, {});

  const visibleProjects =
    activeGroup === "all"
      ? allProjects
      : allProjects.filter((p) => groupForProject(p.id) === activeGroup);

  const openPdfSelector = () => {
    setSelectedProjects(allProjects.map(p => p.id));
    setShowPdfSelector(true);
  };

  const toggleProjectSelection = (projectId: string) => {
    setSelectedProjects(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProjects.length === allProjects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(allProjects.map(p => p.id));
    }
  };

  const handleDownloadPdf = async () => {
    if (selectedProjects.length === 0) return;
    
    setIsGeneratingPdf(true);
    
    const projectsToDownload = allProjects.filter(p => selectedProjects.includes(p.id));
    
    const pdfContainer = document.createElement('div');
    pdfContainer.style.width = '180mm';
    pdfContainer.style.padding = '15mm';
    pdfContainer.style.backgroundColor = '#ffffff';
    pdfContainer.style.color = '#000000';
    pdfContainer.style.fontFamily = 'Arial, sans-serif';
    pdfContainer.style.boxSizing = 'border-box';
    
    // Bio Section - Cover Page
    pdfContainer.innerHTML = `
      <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #6366f1;">
        <img src="/profile-dio-3.jpg" alt="Profile" 
             style="width: 100px; height: 100px; border-radius: 50%; 
                    object-fit: cover; margin-bottom: 15px; border: 3px solid #6366f1;" />
        <h1 style="font-size: 26px; color: #1f2937; margin-bottom: 5px; font-weight: bold;">
          Widyo Tri Cahya Utomo
        </h1>
        <p style="font-size: 14px; color: #6366f1; font-weight: 600; margin-bottom: 6px; font-style: italic;">
          "Dio"
        </p>
        <p style="font-size: 13px; color: #4b5563; margin-bottom: 20px;">
          Data Engineer | Cloud Data Engineer | AI & Web3 Explorer
        </p>
        
        <div style="display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-bottom: 20px;">
          <a href="mailto:widyo1107@gmail.com" style="font-size: 11px; color: #374151; text-decoration: none;">📧 widyo1107@gmail.com</a>
          <a href="https://linkedin.com/in/widyo11" target="_blank" style="font-size: 11px; color: #374151; text-decoration: none;">💼 linkedin.com/in/widyo11</a>
          <a href="https://github.com/tcuber11" target="_blank" style="font-size: 11px; color: #374151; text-decoration: none;">🐙 github.com/tcuber11</a>
          <a href="https://instagram.com/tcu98" target="_blank" style="font-size: 11px; color: #374151; text-decoration: none;">📸 instagram.com/tcu98</a>
        </div>
      </div>
      
      <div style="background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%); padding: 15px; border-radius: 10px; margin-bottom: 20px; border-left: 4px solid #6366f1;">
        <h2 style="color: #1f2937; margin-bottom: 10px; font-size: 16px; font-weight: bold;">About Me</h2>
        <p style="line-height: 1.6; color: #4b5563; font-size: 11px; text-align: justify;">
          ${t.hero.description}
        </p>
      </div>
      
      <div style="margin-bottom: 25px;">
        <h2 style="color: #1f2937; font-size: 16px; font-weight: bold; margin-bottom: 12px; border-bottom: 2px solid #6366f1; padding-bottom: 6px;">🛠️ Skills & Expertise</h2>
        <div style="display: flex; flex-wrap: wrap; gap: 10px;">
          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; border: 1px solid #e5e7eb; flex: 1; min-width: 140px;">
            <h3 style="font-size: 11px; font-weight: bold; color: #4338ca; margin-bottom: 4px;">💾 Databases</h3>
            <p style="font-size: 9px; color: #4b5563; line-height: 1.4;">PostgreSQL, Aurora, Snowflake, Oracle, MongoDB</p>
          </div>
          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; border: 1px solid #e5e7eb; flex: 1; min-width: 140px;">
            <h3 style="font-size: 11px; font-weight: bold; color: #4338ca; margin-bottom: 4px;">☁️ Cloud</h3>
            <p style="font-size: 9px; color: #4b5563; line-height: 1.4;">AWS, GCP, Oracle Cloud</p>
          </div>
          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; border: 1px solid #e5e7eb; flex: 1; min-width: 140px;">
            <h3 style="font-size: 11px; font-weight: bold; color: #4338ca; margin-bottom: 4px;">🔄 ETL & Integration</h3>
            <p style="font-size: 9px; color: #4b5563; line-height: 1.4;">Pentaho, Talend, Alteryx, Dataiku, Denodo</p>
          </div>
        </div>
        <div style="display: flex; flex-wrap: wrap; gap: 10px; margin-top: 10px;">
          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; border: 1px solid #e5e7eb; flex: 1; min-width: 140px;">
            <h3 style="font-size: 11px; font-weight: bold; color: #4338ca; margin-bottom: 4px;">💻 Programming</h3>
            <p style="font-size: 9px; color: #4b5563; line-height: 1.4;">SQL, Python, JavaScript, HTML/CSS, C#</p>
          </div>
          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; border: 1px solid #e5e7eb; flex: 1; min-width: 140px;">
            <h3 style="font-size: 11px; font-weight: bold; color: #4338ca; margin-bottom: 4px;">🤖 AI & Streaming</h3>
            <p style="font-size: 9px; color: #4b5563; line-height: 1.4;">Dataiku ML, Confluent Kafka</p>
          </div>
          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; border: 1px solid #e5e7eb; flex: 1; min-width: 140px;">
            <h3 style="font-size: 11px; font-weight: bold; color: #4338ca; margin-bottom: 4px;">✨ Soft Skills</h3>
            <p style="font-size: 9px; color: #4b5563; line-height: 1.4;">Leadership, Communication, Problem Solving</p>
          </div>
        </div>
      </div>
      
      <div style="margin-bottom: 25px; page-break-before: always;">
        <h2 style="color: #1f2937; font-size: 16px; font-weight: bold; border-bottom: 2px solid #6366f1; padding-bottom: 6px; margin-bottom: 15px;">
          💼 Work Experience
        </h2>
        
        <div style="margin-bottom: 20px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
          <div style="display: flex; align-items: center; gap: 12px; padding: 12px; background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);">
            <img src="/assets/logo-berca.png" alt="Berca" style="width: 40px; height: 40px; object-fit: contain;" onerror="this.style.display='none'" />
            <div>
              <h3 style="font-size: 13px; font-weight: bold; color: #1f2937; margin-bottom: 2px;">PT. Berca Hardayaperkasa</h3>
              <p style="font-size: 11px; color: #6366f1; font-weight: 600;">Data Engineer</p>
              <p style="font-size: 10px; color: #6b7280;">Jun 2022 - Present</p>
            </div>
          </div>
          <div style="padding: 12px;">
            <div style="border-left: 2px solid #6366f1; padding-left: 10px; margin-bottom: 10px;">
              <h4 style="font-size: 11px; font-weight: bold; color: #1f2937; margin-bottom: 4px;">EnterpriseDB Data Migration & Replication</h4>
              <p style="font-size: 9px; color: #4b5563; line-height: 1.5; margin-bottom: 6px;">Database migration from Oracle & SQL Server to PostgreSQL using EDB Migration Toolkit and setting up replication.</p>
              <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">EnterpriseDB</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">Migration Toolkit</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">Replication</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">SQL</span>
              </div>
            </div>
            <div style="border-left: 2px solid #6366f1; padding-left: 10px; margin-bottom: 10px;">
              <h4 style="font-size: 11px; font-weight: bold; color: #1f2937; margin-bottom: 4px;">Data Integration & Virtualization</h4>
              <p style="font-size: 9px; color: #4b5563; line-height: 1.5; margin-bottom: 6px;">ETL development using Talend and data virtualization with Tibco Data Virtualization.</p>
              <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">PostgreSQL</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">Talend</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">Tibco DV</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">Linux</span>
              </div>
            </div>
            <div style="border-left: 2px solid #6366f1; padding-left: 10px;">
              <h4 style="font-size: 11px; font-weight: bold; color: #1f2937; margin-bottom: 4px;">AWS Cloud Migration</h4>
              <p style="font-size: 9px; color: #4b5563; line-height: 1.5; margin-bottom: 6px;">Oracle to Aurora PostgreSQL migration using AWS DMS and Schema Conversion Tool.</p>
              <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">Aurora PostgreSQL</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">AWS DMS</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">AWS SCT</span>
              </div>
            </div>
          </div>
        </div>
        
        <div style="margin-bottom: 15px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
          <div style="display: flex; align-items: center; gap: 12px; padding: 12px; background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);">
            <img src="/assets/logo-maranatha.png" alt="Maranatha" style="width: 40px; height: 40px; object-fit: contain;" onerror="this.style.display='none'" />
            <div>
              <h3 style="font-size: 13px; font-weight: bold; color: #1f2937; margin-bottom: 2px;">Universitas Kristen Maranatha</h3>
              <p style="font-size: 11px; color: #6366f1; font-weight: 600;">Lab Assistant</p>
              <p style="font-size: 10px; color: #6b7280;">Aug 2019 - Mar 2022</p>
            </div>
          </div>
          <div style="padding: 12px;">
            <div style="border-left: 2px solid #6366f1; padding-left: 10px;">
              <h4 style="font-size: 11px; font-weight: bold; color: #1f2937; margin-bottom: 4px;">Laboratory Management & Teaching</h4>
              <p style="font-size: 9px; color: #4b5563; line-height: 1.5; margin-bottom: 6px;">Managing Linux lab, IoT equipment, and 3D printers. Assisted students in practical sessions.</p>
              <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">Linux</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">IoT</span>
                <span style="background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 8px; font-size: 8px;">3D Printing</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div style="margin-bottom: 25px; page-break-before: always;">
        <h2 style="color: #1f2937; font-size: 16px; font-weight: bold; border-bottom: 2px solid #6366f1; padding-bottom: 6px; margin-bottom: 15px;">
          🏆 Professional Certifications
        </h2>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
          <div style="background: #f9fafb; padding: 12px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="font-size: 12px; font-weight: bold; color: #4338ca; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span>🔶</span> Oracle Cloud & AI
            </h3>
            <ul style="font-size: 9px; color: #4b5563; line-height: 1.6; margin: 0; padding-left: 14px;">
              <li>OCI 2025 Multicloud Architect Professional</li>
              <li>OCI 2025 Migration Architect Professional</li>
              <li>OCI 2025 Data Science Professional</li>
              <li>OCI 2025 GenAI Professional</li>
              <li>Oracle AI Vector Search Professional</li>
              <li>Oracle Autonomous Database 2025 Professional</li>
              <li>Oracle Database@AWS Architect Professional</li>
              <li>OCI 2025 AI Foundations Associate</li>
              <li>OCI 2025 Foundations Associate</li>
              <li>Data Platform 2025 Foundations Associate</li>
            </ul>
          </div>
          
          <div style="background: #f9fafb; padding: 12px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="font-size: 12px; font-weight: bold; color: #4338ca; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span>🐘</span> PostgreSQL & EDB
            </h3>
            <ul style="font-size: 9px; color: #4b5563; line-height: 1.6; margin: 0; padding-left: 14px;">
              <li>EDB Postgres AI Database v17 Advanced</li>
              <li>EDB Postgres AI Database v17 Essentials</li>
              <li>PostgreSQL v16 Advanced</li>
              <li>PostgreSQL v16 Essentials</li>
              <li>EDB Postgres 15 Advanced</li>
              <li>EDB Postgres 15 Essentials</li>
            </ul>
          </div>
          
          <div style="background: #f9fafb; padding: 12px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="font-size: 12px; font-weight: bold; color: #4338ca; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span>🔧</span> Alteryx
            </h3>
            <ul style="font-size: 9px; color: #4b5563; line-height: 1.6; margin: 0; padding-left: 14px;">
              <li>Alteryx Designer Advanced</li>
              <li>Alteryx Designer Core</li>
              <li>Alteryx Server Administration</li>
              <li>Alteryx Server Implementation</li>
              <li>Alteryx Designer Cloud Core</li>
              <li>Alteryx Designer Cloud Trifacta Core</li>
              <li>Alteryx Designer Cloud Trifacta Administrator</li>
            </ul>
          </div>
          
          <div style="background: #f9fafb; padding: 12px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="font-size: 12px; font-weight: bold; color: #4338ca; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span>🤖</span> Dataiku
            </h3>
            <ul style="font-size: 9px; color: #4b5563; line-height: 1.6; margin: 0; padding-left: 14px;">
              <li>Dataiku Advanced Designer</li>
              <li>Dataiku Core Designer</li>
              <li>Dataiku Developer</li>
              <li>Dataiku ML Practitioner</li>
              <li>Dataiku MLOps Practitioner</li>
              <li>Dataiku Generative AI Practitioner</li>
            </ul>
          </div>
          
          <div style="background: #f9fafb; padding: 12px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="font-size: 12px; font-weight: bold; color: #4338ca; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span>🔗</span> Denodo
            </h3>
            <ul style="font-size: 9px; color: #4b5563; line-height: 1.6; margin: 0; padding-left: 14px;">
              <li>Denodo Platform 9 Certified Architect Associate</li>
              <li>Denodo Platform 8.0 Certified Architect Associate</li>
            </ul>
          </div>
          
          <div style="background: #f9fafb; padding: 12px; border-radius: 8px; border: 1px solid #e5e7eb;">
            <h3 style="font-size: 12px; font-weight: bold; color: #4338ca; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
              <span>📊</span> Integration & Analytics
            </h3>
            <ul style="font-size: 9px; color: #4b5563; line-height: 1.6; margin: 0; padding-left: 14px;">
              <li>Astronomer Certification for Apache Airflow 3</li>
              <li>Confluent Data Streaming Engineer</li>
              <li>Fivetran Connector SDK Certification</li>
              <li>Qlik Certified Partner Sales – Analytics</li>
              <li>Qlik Certified Partner Sales – Data & Analytics</li>
              <li>Qlik Certified Partner Sales – Data Integration</li>
            </ul>
          </div>
        </div>
        
        <p style="font-size: 9px; color: #6b7280; text-align: center; margin-top: 12px; font-style: italic;">
          📜 Total 40+ Professional Certifications • View all credentials at tcuportfolio.lovable.app
        </p>
      </div>
      
      <div style="margin-bottom: 20px;">
        <h2 style="color: #1f2937; font-size: 16px; font-weight: bold; border-bottom: 2px solid #6366f1; padding-bottom: 6px; margin-bottom: 15px;">
          📁 Selected Projects
        </h2>
      </div>
    `;
    
    // Project Cards
    for (const project of projectsToDownload) {
      const projectHtml = `
        <div style="margin-bottom: 25px; page-break-inside: avoid; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
          <div style="width: 100%; aspect-ratio: 16/9; overflow: hidden; background: #f3f4f6;">
            <img src="${project.thumbnail}" alt="${project.title}" style="width: 100%; height: 100%; object-fit: contain; object-position: center;" />
          </div>
          <div style="padding: 15px;">
            <h3 style="font-size: 15px; color: #1f2937; margin-bottom: 8px; font-weight: bold;">${project.title}</h3>
            <p style="font-size: 11px; color: #4b5563; margin-bottom: 12px; line-height: 1.6;">${project.description}</p>
            <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px;">
              ${project.tags.map(tag => `<span style="background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%); color: #4338ca; padding: 4px 10px; border-radius: 12px; font-size: 9px; font-weight: 500;">${tag}</span>`).join('')}
            </div>
            ${project.link ? `<p style="font-size: 10px; color: #6366f1; font-weight: 500;">🔗 ${project.link}</p>` : '<p style="font-size: 10px; color: #9ca3af; font-style: italic;">Interactive Demo Available on Website</p>'}
          </div>
        </div>
      `;
      pdfContainer.innerHTML += projectHtml;
    }
    
    // Footer
    pdfContainer.innerHTML += `
      <div style="text-align: center; margin-top: 50px; padding-top: 25px; border-top: 2px solid #e5e7eb;">
        <a href="https://tcuportfolio.vercel.app" target="_blank" style="font-size: 13px; color: #6366f1; font-weight: 500; margin-bottom: 5px; text-decoration: none; display: block;">🌐 tcuportfolio.vercel.app</a>
        <p style="font-size: 11px; color: #9ca3af;">Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <p style="font-size: 10px; color: #d1d5db; margin-top: 10px;">© ${new Date().getFullYear()} Widyo Tri Cahya Utomo. All rights reserved.</p>
      </div>
    `;
    
    document.body.appendChild(pdfContainer);
    
    const opt = {
      margin: 10,
      filename: 'Widyo_Portfolio.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, allowTaint: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    try {
      await html2pdf().set(opt).from(pdfContainer).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      document.body.removeChild(pdfContainer);
      setIsGeneratingPdf(false);
      setShowPdfSelector(false);
    }
  };

  return (
    <section id="portfolio" className="py-12 sm:py-16 md:py-20 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">{t.portfolio.title}</h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-6">{t.portfolio.subtitle}</p>
          {user && isAdmin && (
            <Button 
              onClick={openPdfSelector} 
              variant="outline"
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {t.portfolio.downloadPdf}
            </Button>
          )}
        </div>

        <div
          className="flex flex-wrap justify-center gap-2 mb-8 max-w-4xl mx-auto"
          role="group"
          aria-label="Filter projects by group"
        >
          {(["all", ...PROJECT_GROUPS] as const).map((group) => {
            const isActive = activeGroup === group;
            const count = group === "all" ? allProjects.length : (groupCounts[group] ?? 0);
            if (group !== "all" && count === 0) return null;
            return (
              <button
                key={group}
                type="button"
                onClick={() => setActiveGroup(group)}
                aria-pressed={isActive}
                className={`px-3.5 py-2 rounded-full text-sm border transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:text-foreground hover:border-primary/50"
                }`}
              >
                {group === "all" ? "All" : group}
                <span className={`ml-1.5 text-xs ${isActive ? "opacity-80" : "opacity-60"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 max-w-6xl mx-auto">
          {visibleProjects.map((project) => (
            <Card
              key={project.id}
              // Entrance is handled by scroll-reveal-scale alone. Carrying
              // animate-fade-up as well meant two competing opacity mechanisms, and
              // the keyframe restarted from opacity 0 on every filter change — the
              // grid read as empty until it finished.
              className="shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden hover-glow scroll-reveal-scale"
            >
              <Carousel
                className="w-full relative"
                opts={{ loop: true }}
                setApi={(api) => handleCarouselApiSet(project.id, api)}
              >
                <div>
                  <CarouselContent>
                    {project.galleryImages.length > 0 ? (
                      project.galleryImages.map((image, imgIndex) => (
                        <CarouselItem key={imgIndex}>
                          <div className="aspect-video">
                            <img
                              src={image}
                              alt={`${project.title} - Image ${imgIndex + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        </CarouselItem>
                      ))
                    ) : (
                      <CarouselItem>
                        <div className="aspect-video">
                          <img 
                            src={project.thumbnail} 
                            alt={project.title} 
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      </CarouselItem>
                    )}
                  </CarouselContent>
                </div>
                {project.galleryImages.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-50 h-8 w-8 rounded-full bg-background/90 hover:bg-background border shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        carouselApisRef.current[project.id]?.scrollPrev();
                      }}
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-50 h-8 w-8 rounded-full bg-background/90 hover:bg-background border shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        carouselApisRef.current[project.id]?.scrollNext();
                      }}
                      aria-label="Next image"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 pointer-events-auto">
                      {project.galleryImages.map((_, dotIndex) => (
                        <button
                          key={dotIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            carouselApisRef.current[project.id]?.scrollTo(dotIndex);
                          }}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            (currentSlides[project.id] ?? 0) === dotIndex
                              ? "bg-primary w-6"
                              : "bg-primary/40 hover:bg-primary/60"
                          }`}
                          aria-label={`Go to slide ${dotIndex + 1} of ${project.galleryImages.length}`}
                          aria-current={(currentSlides[project.id] ?? 0) === dotIndex ? "true" : "false"}
                        />
                      ))}
                    </div>
                  </>
                )}
              </Carousel>
              <CardHeader>
                <CardTitle className="text-xl">{project.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, tIndex) => (
                    <span key={tIndex} className="text-xs px-2 py-1 rounded-md bg-primary/10 text-primary">
                      {tag}
                    </span>
                  ))}
                </div>
                {project.isInteractive ? (
                  <Button
                    className="w-full"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPenguinExplorer(true);
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t.portfolio.explore}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/portfolio/${project.id}`);
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={showPenguinExplorer} onOpenChange={setShowPenguinExplorer}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.portfolio.dataProjects[0].title}</DialogTitle>
          </DialogHeader>
          <PenguinExplorer />
        </DialogContent>
      </Dialog>

      <Dialog open={showPdfSelector} onOpenChange={setShowPdfSelector}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.portfolio.selectProjects}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2 pb-2 border-b">
              <Checkbox 
                id="select-all"
                checked={selectedProjects.length === allProjects.length}
                onCheckedChange={toggleSelectAll}
              />
              <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                {t.portfolio.selectAll}
              </label>
            </div>
            {allProjects.map((project) => (
              <div key={project.id} className="flex items-center space-x-3">
                <Checkbox 
                  id={project.id}
                  checked={selectedProjects.includes(project.id)}
                  onCheckedChange={() => toggleProjectSelection(project.id)}
                />
                <label htmlFor={project.id} className="flex items-center gap-3 cursor-pointer flex-1">
                  <img 
                    src={project.thumbnail} 
                    alt={project.title} 
                    className="w-12 h-8 object-cover rounded"
                  />
                  <span className="text-sm">{project.title}</span>
                </label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPdfSelector(false)}>
              {t.portfolio.cancel}
            </Button>
            <Button 
              onClick={handleDownloadPdf} 
              disabled={selectedProjects.length === 0 || isGeneratingPdf}
              className="gap-2"
            >
              {isGeneratingPdf ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t.portfolio.generatingPdf}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  {t.portfolio.downloadSelected} ({selectedProjects.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Portfolio;
